import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiClientService } from '../../core/api-client.service';

export type SellerOnboardingStepKey = string;

export interface SellerOnboardingStep {
  stepProcessReminderFormID?: number;
  stepKey: SellerOnboardingStepKey;
  title: string;
  description?: string | null;
  formID?: number;
  isRequired?: boolean;
  displayOrder?: number;
  formFields?: unknown[];
  answer?: Record<string, unknown>;
  isCompleted?: boolean;
}

export interface SellerOnboardingField {
  id?: number;
  formKey?: string;
  type?: string;
  options?: unknown;
  required?: boolean | number;
  minlenght?: number | string | null;
  maxlenght?: number | string | null;
  showDisplay?: boolean | number;
  conditionFieldKey?: string | null;
  conditionOperator?: string | null;
  conditionValue?: string | null;
  title?: string;
  description?: string | null;
}

export interface SellerOnboardingPayload {
  result?: boolean;
  sellerID?: number;
  code?: string;
  message?: string;
  missingStep?: SellerOnboardingStepKey;
  reminderName?: string;
  title?: string;
  currentStep?: SellerOnboardingStepKey;
  completedSteps?: SellerOnboardingStepKey[];
  steps?: SellerOnboardingStep[];
  formData?: Record<string, unknown>;
  sellerInfo?: Record<string, unknown>;
  awaitingApproval?: boolean;
  postOnboarding?: { currentStep?: string | null; completedSteps?: string[]; completed?: boolean };
}

export class SellerOnboardingSaveError extends Error {
  constructor(message: string, readonly fieldErrors: Record<string, string>) {
    super(message);
  }
}

@Injectable({ providedIn: 'root' })
export class SellerOnboardingService {
  private readonly api = inject(ApiClientService);

  async get(): Promise<SellerOnboardingPayload> {
    const response = await firstValueFrom(this.api.post<unknown>('sellerOnboarding', {
      id: 'sellerOnboarding',
      reminderName: 'sellerRegister'
    }));
    return this.extractPayload(response, 'sellerOnboarding');
  }

  async quickStart(payload: { shopName: string }): Promise<SellerOnboardingPayload> {
    const response = await firstValueFrom(this.api.post<unknown>('sellerQuickStart', payload));
    return this.extractPayload(response, 'sellerQuickStart');
  }

  async saveStep(stepKey: SellerOnboardingStepKey, nextStep: SellerOnboardingStepKey, formData: Record<string, unknown>): Promise<SellerOnboardingPayload> {
    const response = await firstValueFrom(this.api.post<unknown>('sellerOnboardingSave', {
      processKey: 'sellerOnboarding',
      reminderName: 'sellerRegister',
      stepKey,
      nextStep,
      formData
    }));
    const result = this.extractPayload(response, 'sellerOnboardingProgress');
    if (!result.result) {
      throw new SellerOnboardingSaveError(
        result.message || 'Başvuru adımı kaydedilemedi.',
        this.extractErrors(response)
      );
    }
    return result;
  }

  async complete(): Promise<SellerOnboardingPayload> {
    const response = await firstValueFrom(this.api.post<unknown>('sellerOnboardingComplete', {
      processKey: 'sellerOnboarding',
      reminderName: 'sellerRegister'
    }));
    return this.extractPayload(response, 'sellerOnboardingComplete');
  }

  async savePostStep(stepKey: string): Promise<SellerOnboardingPayload> {
    const response = await firstValueFrom(this.api.post<unknown>('sellerOnboardingPostStep', {
      processKey: 'sellerOnboarding',
      stepKey
    }));
    const result = this.extractPayload(response, 'sellerOnboardingPostStep');
    if (!result.result) {
      throw new Error(result.message || 'Başlangıç adımı kaydedilemedi.');
    }
    return result;
  }

  async checkShopName(shopName: string): Promise<{ result: boolean; message?: string; shopName?: string }> {
    const response = await firstValueFrom(this.api.post<any>('/api/seller/shop-name/check', { shopName }));
    const data = response?.data ?? response ?? {};
    return {
      result: !!(data.result ?? response?.result ?? response?.success),
      message: data.message ?? response?.message,
      shopName: data.shopName
    };
  }

  async reset(): Promise<boolean> {
    const response = await firstValueFrom(this.api.post<unknown>('sellerOnboardingReset', {
      processKey: 'sellerOnboarding',
      reminderName: 'sellerRegister'
    }));
    return !!this.extractPayload(response, 'sellerOnboardingReset')?.result;
  }

  private extractPayload(response: unknown, key: string): SellerOnboardingPayload {
    const stack = Array.isArray(response) ? [...response] : [response];
    while (stack.length) {
      const item = stack.shift();
      if (!item || typeof item !== 'object') {
        continue;
      }
      const record = item as Record<string, unknown>;
      if (this.isRecord(record[key])) {
        return record[key] as SellerOnboardingPayload;
      }
      if (this.isRecord(record['apiData']) && this.isRecord((record['apiData'] as Record<string, unknown>)[key])) {
        return (record['apiData'] as Record<string, unknown>)[key] as SellerOnboardingPayload;
      }
      for (const value of Object.values(record)) {
        if (value && typeof value === 'object') {
          stack.push(value);
        }
      }
    }
    return {};
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  private extractErrors(response: unknown): Record<string, string> {
    const found: Record<string, string> = {};
    const stack = Array.isArray(response) ? [...response] : [response];
    while (stack.length) {
      const item = stack.shift();
      if (!item || typeof item !== 'object') {
        continue;
      }
      const record = item as Record<string, unknown>;
      if (this.isRecord(record['errors'])) {
        for (const [key, error] of Object.entries(record['errors'])) {
          if (this.isRecord(error)) {
            found[key] = String(error['message'] || error['code'] || 'Geçersiz alan.');
          } else {
            found[key] = String(error || 'Geçersiz alan.');
          }
        }
      }
      for (const value of Object.values(record)) {
        if (value && typeof value === 'object') {
          stack.push(value);
        }
      }
    }
    return found;
  }
}
